var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import styled from 'styled-components';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, FormHelperText, Grid, IconButton, TextField, Typography } from '@material-ui/core';
import { Add, CancelOutlined, ExpandMore, RemoveCircleOutlineOutlined } from '@material-ui/icons';
import { Autocomplete } from '@material-ui/lab';
import { AppSnackbar, ConfigFieldControl, ConfigTextField, useDebounce, useFromLocalCache, useTimeout } from '@sierralabs/core-ui';
import { useQueryContext } from '@sierralabs/query-ui';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import SiteQueryPicker from '../../common/pickers/SiteQueryPicker';
import SiteRouteAndParamPicker, { updateRouteAndParam } from '../../common/pickers/SiteRouteAndParamPicker';
import TraceBoard from '../../common/trace-board/TraceBoard';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useFindNearestRouteComponent } from '../../hooks/site-route/useFindNearestRouteComponent';
import { getRouteLocalCacheKey } from '../../hooks/site-route/useRouteLocalCacheKey';
import { useGetSiteTraceBoard } from '../../hooks/site-trace-board/useGetSiteTraceBoard';
import { useSiteTraceBoard } from '../../hooks/site-trace-board/useSiteTraceBoard';
import { useSiteTraceBoardColumns } from '../../hooks/site-trace-board/useSiteTraceBoardColumns';
import { useSiteTraceBoardParams } from '../../hooks/site-trace-board/useSiteTraceBoardParams';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const itemFieldOptions = [
    { key: 'id', label: 'ID Field', helperText: 'Field for Card ID' },
    { key: 'title', label: 'Title Field', helperText: 'Field for Card Title' },
    {
        key: 'description',
        label: 'Description Field',
        helperText: 'Field for Card Description'
    },
    {
        key: 'status',
        label: 'Status Field',
        helperText: 'Field for Card Status Label'
    },
    {
        key: 'workflowId',
        label: 'Workflow ID Field',
        helperText: 'Field for Card Status Color'
    },
    {
        key: 'deleted',
        label: 'Deleted Field',
        helperText: 'Field for Card Deleted styling'
    }
];
const StyledDiv = styled.div `
  .MuiAccordion-root,
  .MuiAccordion-root.Mui-expanded {
    margin: 0 -12px;
  }
  .MuiAccordionSummary-root {
    max-height: 50px;
    padding-left: 12px;
    padding-right: 12px;
    &.Mui-expanded {
      min-height: unset;
    }
  }
  .MuiAccordionDetails-root {
    padding-left: 12px;
    padding-right: 12px;
    padding-bottom: 12px;
  }
  .config-field-control {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
  .delete-button {
    margin-right: 8px;
  }
  .column-title {
    display: flex;
    align-items: center;
    width: 100%;
  }
  .linked-columns-grid {
    border: 3px solid ${props => props.theme.palette.divider};
    border-radius: 4px;
    margin-bottom: 12px;
    margin-top: 12px !important;
  }
  .buttons {
    display: flex;
    margin-top: 10px;
    .remove-icon {
      margin-right: 5px;
    }
  }
  .field-content {
    flex: 1;
    padding-right: 0;
  }
  .delete-field-grid {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding-left: 0;
  }
  .details {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
`;
export const SiteTraceBoard = props => {
    var _a;
    const didFetchRef = useRef(false);
    const [search] = useQueryParam('search', withDefault(StringParam, ''));
    const debouncedSearch = useDebounce(search, 300);
    const [error, setError] = useState();
    const [debounceGet] = useTimeout();
    const component = useSubscribeComponentChanges(SiteTraceBoardPlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const { columns: boardColumns, getRows, setup, setItems } = useSiteTraceBoard(component);
    const boardParams = useSiteTraceBoardParams(component);
    const { columns } = useSiteTraceBoardColumns(component, boardColumns);
    const { isLoading, error: getError, getBoard, setBoardLoading } = useGetSiteTraceBoard();
    const localCache = useFromLocalCache();
    const route = useFindNearestRouteComponent(component);
    const classes = ['site-trace-board'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    // add heading for rows
    const rows = useMemo(() => {
        const { rows, orphanRow } = getRows();
        // only add heading to last row (orphanRow) if other rows exist
        if (rows.length > 0) {
            orphanRow.heading = 'Orphaned Cards';
        }
        return rows.concat(orphanRow);
    }, [getRows]);
    const allQueryIds = (((_a = component.props) === null || _a === void 0 ? void 0 : _a.columns) || [])
        .map(column => column.queryId)
        .filter((queryId) => !!queryId);
    const queryIds = useMemo(() => {
        return Array.from(allQueryIds);
        // re-evaluate only when component queryIds change
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allQueryIds.join(',')]);
    const expressionMaps = useMemo(() => {
        const expressionMaps = {};
        for (const queryId of queryIds) {
            const key = getRouteLocalCacheKey(route, { queryId });
            const query = localCache.get(key);
            if (query === null || query === void 0 ? void 0 : query.config) {
                expressionMaps[queryId] = query.config;
            }
        }
        return expressionMaps;
    }, [localCache, queryIds, route]);
    const getBoardWithFilters = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setBoardLoading(true);
        // since board size may be large, avoid storing it into
        // a state to save up memory since it is only used once
        const board = yield getBoard(component.id, {
            expressionMaps,
            params: boardParams,
            search: debouncedSearch
        });
        // only stop loading after set items state
        if (board) {
            setup(board, () => setBoardLoading(false));
        }
        else {
            setBoardLoading(false);
        }
    }), [
        boardParams,
        component.id,
        debouncedSearch,
        expressionMaps,
        getBoard,
        setBoardLoading,
        setup
    ]);
    // during edit mode, debounce fetching to handle
    // cases where component is rendering multiple times
    useEffect(() => {
        // always no debounce for the first fetch
        if (!didFetchRef.current || !editing) {
            didFetchRef.current = true;
            getBoardWithFilters();
        }
        else {
            return debounceGet(() => {
                getBoardWithFilters();
            }, 200);
        }
    }, [editing, debounceGet, getBoardWithFilters]);
    // set fetch error only when viewing
    useEffect(() => {
        !editing && setError(getError);
    }, [editing, getError]);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteTraceBoardPlugin }, props),
        React.createElement(TraceBoard, { rows: rows, columns: columns, isLoading: isLoading, className: classes.join(' '), containerProps: hoverProps(component), setItems: setItems }),
        React.createElement(AppSnackbar, { severity: 'error', open: !!error, onClose: () => setError(undefined) }, (error === null || error === void 0 ? void 0 : error.message) || 'An error occurred.')));
};
export const SiteTraceBoardConfigurator = props => {
    var _a;
    const { component, update, insert, remove } = useConfigComponent(props.component);
    const { queries } = useQueryContext();
    const [expanded, setExpanded] = useState();
    const columns = (_a = component.props) === null || _a === void 0 ? void 0 : _a.columns;
    const getQueryOptions = (queryId) => {
        let queryOptions = [];
        if (queryId) {
            const query = queries.find(query => query.id === queryId);
            queryOptions = query ? [query] : [];
        }
        return queryOptions;
    };
    const handleAddLinkedColumnField = (colIndex, linkedColIndex) => {
        var _a;
        const linkedColumnFields = (_a = columns === null || columns === void 0 ? void 0 : columns[colIndex].linkedColumns) === null || _a === void 0 ? void 0 : _a[linkedColIndex].fields;
        const path = `props.columns[${colIndex}].linkedColumns.[${linkedColIndex}].fields`;
        if (!linkedColumnFields) {
            update(path, []);
        }
        insert(path, { source: '', target: '' });
    };
    return (React.createElement(StyledDiv, null,
        React.createElement(Button, { fullWidth: true, size: 'small', variant: 'outlined', startIcon: React.createElement(Add, null), onClick: () => {
                if (!columns) {
                    update('props.columns', []);
                }
                insert('props.columns', {
                    id: uuid(),
                    name: `Column ${!columns ? 1 : columns.length + 1}`
                });
            } }, "Add Column"),
        React.createElement(Box, { my: 1 }, !columns || columns.length === 0 ? (React.createElement(Typography, { color: 'textSecondary' }, "No Columns configured.")) : (React.createElement(Typography, { variant: 'overline', color: 'textSecondary' }, "Columns"))), columns === null || columns === void 0 ? void 0 :
        columns.map((column, index) => {
            var _a, _b, _c, _d, _e, _f;
            const linkedColumns = column.linkedColumns || [];
            const availableColumns = columns
                .slice(0, index)
                .filter(column => !linkedColumns.some(c => c.id === column.id));
            const queryOptions = getQueryOptions(column.queryId);
            return (React.createElement(Accordion, { key: column.id, expanded: column.id === expanded, onChange: (_, expanded) => setExpanded(expanded ? column.id : undefined) },
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
                    React.createElement(IconButton, { size: 'small', className: 'delete-button', onClick: event => {
                            event.stopPropagation();
                            remove(`props.columns[${index}]`);
                        } },
                        React.createElement(RemoveCircleOutlineOutlined, { fontSize: 'small' })),
                    React.createElement(Typography, { className: 'column-title', variant: 'body2', align: 'center' }, column.name)),
                React.createElement(AccordionDetails, null,
                    React.createElement("div", { className: 'details' },
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(ConfigTextField, { fullWidth: true, label: 'Name', value: column.name || '', onChange: event => {
                                    update(`props.columns[${index}].name`, event.target.value);
                                } })),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryPicker, { label: 'Query', value: column.queryId ? [column.queryId] : [], onChange: queries => {
                                    var _a;
                                    const queryId = (_a = queries.slice(-1).pop()) === null || _a === void 0 ? void 0 : _a.id;
                                    update(`props.columns[${index}].queryId`, queryId);
                                } })),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Search Filter Columns")),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { multiple: true, hideQueryName: true, queryId: column.queryId, queryField: column.searchFields || [], options: queryOptions, onChange: value => {
                                    const fields = value.map(v => v.queryField);
                                    update(`props.columns[${index}].searchFields`, fields);
                                } })),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Card Fields")),
                        itemFieldOptions.map(field => {
                            var _a;
                            return (React.createElement(ConfigFieldControl, { key: field.key },
                                React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: field.label, queryId: column.queryId, queryField: (_a = column.itemFields) === null || _a === void 0 ? void 0 : _a[field.key], onChange: value => {
                                        update(`props.columns[${index}].itemFields[${field.key}]`, value.queryField);
                                    } }),
                                React.createElement(FormHelperText, null, field.helperText)));
                        }),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Button"),
                            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                                "The selected ",
                                React.createElement("strong", null, "route"),
                                " will be navigated when clicking card button")),
                        React.createElement(SiteRouteAndParamPicker, { component: component, routeId: (_a = column.routeNavigation) === null || _a === void 0 ? void 0 : _a.routeId, routeParam: (_b = column.routeNavigation) === null || _b === void 0 ? void 0 : _b.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                                updateRouteAndParam(value, `props.columns[${index}].routeNavigation.routeId`, `props.columns[${index}].routeNavigation.routeParam`).forEach(([path, value]) => update(path, value));
                            } }),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: 'Select a Field', queryId: column.queryId, queryField: (_c = column.routeNavigation) === null || _c === void 0 ? void 0 : _c.field, onChange: value => {
                                    update(`props.columns[${index}].routeNavigation.field`, value.queryField);
                                } })),
                        React.createElement(Box, { mt: 1 },
                            React.createElement(Typography, { variant: 'overline' }, "Filter"),
                            React.createElement(Typography, { variant: 'body1', color: 'textSecondary' },
                                "Filter items by matching the selected",
                                ' ',
                                React.createElement("strong", null, "route param"),
                                " and",
                                ' ',
                                React.createElement("strong", null, "query field"),
                                ".")),
                        React.createElement(SiteRouteAndParamPicker, { nearestOnly: true, component: component, routeId: (_d = column.filter) === null || _d === void 0 ? void 0 : _d.routeId, routeParam: (_e = column.filter) === null || _e === void 0 ? void 0 : _e.routeParam, siteRoutePickerProps: { label: 'Route' }, onChange: value => {
                                updateRouteAndParam(value, `props.columns[${index}].filter.routeId`, `props.columns[${index}].filter.routeParam`).forEach(([path, value]) => update(path, value));
                            } }),
                        React.createElement(ConfigFieldControl, null,
                            React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: 'Select a Field', queryId: column.queryId, queryField: (_f = column.filter) === null || _f === void 0 ? void 0 : _f.field, onChange: value => {
                                    update(`props.columns[${index}].filter.field`, value.queryField);
                                } })),
                        index !== 0 && (React.createElement(React.Fragment, null,
                            React.createElement(Box, { mt: 1 },
                                React.createElement(Typography, { variant: 'overline' }, "Linked Columns")),
                            linkedColumns.map((linkedCol, linkedColIndex) => {
                                const fields = linkedCol.fields || [];
                                const connectedColumn = columns.find(c => c.id === linkedCol.id);
                                const basePath = `props.columns[${index}].linkedColumns[${linkedColIndex}]`;
                                return (React.createElement(Grid, { container: true, spacing: 2, key: `${linkedCol.id}-${linkedColIndex}` },
                                    React.createElement(Grid, { item: true, xs: 12, className: 'linked-columns-grid' },
                                        React.createElement(ConfigFieldControl, null,
                                            React.createElement(Autocomplete, { fullWidth: true, value: connectedColumn || null, options: availableColumns, getOptionLabel: option => option.name || '', getOptionSelected: (option, value) => {
                                                    return option.id === value.id;
                                                }, onChange: (_, value) => {
                                                    update(`${basePath}.id`, value === null || value === void 0 ? void 0 : value.id);
                                                    update(`${basePath}.fields`, []);
                                                    if (value) {
                                                        handleAddLinkedColumnField(index, linkedColIndex);
                                                    }
                                                }, renderInput: params => (React.createElement(TextField, Object.assign({}, params, { fullWidth: true, size: 'small', label: 'Linked Column', placeholder: 'Select column to link', InputProps: Object.assign(Object.assign({}, params.InputProps), { disableUnderline: true }) }))) })),
                                        fields.map((field, fieldIndex) => (React.createElement(Fragment, { key: `${linkedCol.id}-${linkedColIndex}-${fieldIndex}` },
                                            React.createElement(Grid, { container: true, spacing: 2 },
                                                React.createElement(Grid, { item: true, className: 'field-content' },
                                                    React.createElement(ConfigFieldControl, null,
                                                        React.createElement(SiteQueryFieldPicker, { options: queryOptions, label: 'Source Field', queryId: column.queryId, queryField: field.source, onChange: value => {
                                                                update(`${basePath}.fields[${fieldIndex}].source`, value.queryField);
                                                            } })),
                                                    React.createElement(ConfigFieldControl, null,
                                                        React.createElement(SiteQueryFieldPicker, { options: getQueryOptions(connectedColumn === null || connectedColumn === void 0 ? void 0 : connectedColumn.queryId), label: 'Target Field', queryId: connectedColumn === null || connectedColumn === void 0 ? void 0 : connectedColumn.queryId, queryField: field.target, onChange: value => {
                                                                update(`${basePath}.fields[${fieldIndex}].target`, value.queryField);
                                                            } }))),
                                                React.createElement(Grid, { item: true, className: 'delete-field-grid' },
                                                    React.createElement(IconButton, { size: 'small', onClick: e => {
                                                            e.stopPropagation();
                                                            remove(`${basePath}.fields[${fieldIndex}]`);
                                                        } },
                                                        React.createElement(RemoveCircleOutlineOutlined, { fontSize: 'small' })))),
                                            fieldIndex < fields.length - 1 && (React.createElement(Box, { mt: 2 },
                                                React.createElement(Divider, null)))))),
                                        React.createElement("div", { className: 'buttons' },
                                            React.createElement(IconButton, { size: 'small', className: 'remove-icon', onClick: e => {
                                                    e.stopPropagation();
                                                    remove(basePath);
                                                } },
                                                React.createElement(CancelOutlined, { fontSize: 'small' })),
                                            React.createElement(Button, { fullWidth: true, size: 'small', variant: 'outlined', disabled: !(linkedCol === null || linkedCol === void 0 ? void 0 : linkedCol.id), startIcon: React.createElement(Add, null), onClick: () => {
                                                    handleAddLinkedColumnField(index, linkedColIndex);
                                                } }, "Add Field")))));
                            }),
                            React.createElement(Button, { fullWidth: true, size: 'small', variant: 'outlined', disabled: availableColumns.length === 0, startIcon: React.createElement(Add, null), onClick: () => {
                                    const path = `props.columns[${index}].linkedColumns`;
                                    if (!column.linkedColumns) {
                                        update(path, []);
                                    }
                                    insert(path, { id: '' });
                                } }, "Link Column")))))));
        })));
};
export const SiteTraceBoardPlugin = {
    name: 'Site Trace Board',
    type: 'site-trace-board',
    component: SiteTraceBoard,
    configurator: SiteTraceBoardConfigurator
};
//# sourceMappingURL=SiteTraceBoardPlugin.js.map